import type { RouteLocationNormalized } from 'vue-router'

import ability from '@/plugins/casl/ability'
import type { Actions, Subjects } from '@/plugins/casl/AppAbility'
import { useUserStore } from '@/stores/user'
import type { NavGroup, NavLink } from '@layouts/types'

export function can(action: string | undefined, subject: string | undefined): boolean {
  if (!action || !subject)
    return true

  return ability.can(action as Actions, subject as Subjects)
}

/**
 * @deprecated Use nav item action/subject with ability.can() instead.
 * Kept for vertical nav and route meta that haven't migrated yet.
 */
export function hasPermission(permissions?: string[]): boolean {
  const user = useUserStore()

  return !permissions || user.hasPermission(permissions)
}

export function hasResource(resource?: string): boolean {
  const user = useUserStore()

  return !(!user.data?.access?.vector && resource === 'vector')
}

export function canViewNavMenuGroup(item: NavGroup): boolean {
  if (item.action && item.subject)
    return can(item.action, item.subject) && item.children.some(child => canViewNavItem(child))

  // Legacy fallback: permissions string array check
  const hasAnyVisibleChild = item.children.some(i => hasPermission(i.permissions))

  if (!hasResource(item.resource))
    return false

  if (!item.permissions)
    return hasAnyVisibleChild

  return hasPermission(item.permissions) && hasAnyVisibleChild
}

function canViewNavItem(item: NavLink | NavGroup): boolean {
  if ('children' in item)
    return canViewNavMenuGroup(item)

  if (item.action && item.subject)
    return can(item.action, item.subject)

  return hasPermission(item.permissions) && hasResource(item.resource)
}

export function canNavigate(to: RouteLocationNormalized): boolean {
  // Check the leaf (most-specific) route first; fall back through ancestors.
  // This prevents a parent route with no metadata from granting blanket access.
  const matched = [...to.matched].reverse()

  for (const route of matched) {
    const { action, subject } = route.meta

    if (action && subject)
      return ability.can(action as Actions, subject as Subjects)

    // Routes with no action/subject and no permissions requirement are always accessible
    // (public pages, 404, etc.). Only block when permissions are explicitly required.
    if (route.meta.permissions)
      return hasPermission(route.meta.permissions as string[])
  }

  // No matched route has any access constraint — allow through.
  return true
}
